export const TAX_CATEGORIES = [
  {
    value: "SMB_ZERO",
    label: "IDR 0 to IDR 500 million",
  },
  {
    value: "SMB",
    label: "IDR 500 million to IDR 4.8 billion",
  },
  {
    value: "ENTERPRISE",
    label: "Above IDR 4.8 Billion",
  },
];

export const taxTypesOptions = [
  { label: "Sales", value: "SALES" },
  { label: "Purchase", value: "PURCHASES" },
];

// Same bounds as the createBusinessTax/updateBusinessTax resolvers. 200 is the
// highest PPnBM rate. Negative rates are still accepted because that is how
// withholding was entered before there was a flag for it — but a tax that
// declares itself withholding must use a positive rate, which is what the
// resolver now enforces too.
export const TAX_PERCENTAGE_MIN = -100;
export const TAX_PERCENTAGE_MAX = 200;

// The backend accepts at most five taxes on one invoice line.
export const MAX_ITEM_TAXES = 5;

// How many of a business's taxes to pull when a read-only screen only needs
// them to put names to the taxes an invoice line already carries. High enough
// to cover any real chart of taxes in one round trip.
export const TAX_DIRECTORY_LIMIT = 100;

// BusinessTax.kind — what the tax actually is under Indonesian tax law, as
// opposed to `type`, which is only the direction (sales or purchases). Optional
// on the record; it drives the withholding default and the default account.
// Labels are i18n keys: FormSelect runs them through t() and falls back to the
// literal.
export const TAX_KINDS = [
  "PPN",
  "PPNBM",
  "PPH_23",
  "PPH_4_2",
  "PPH_22",
  "PPH_15",
  "OTHER",
];

export const taxKindOptions = TAX_KINDS.map((value) => ({
  value,
  label: `taxes.kinds.${value.toLowerCase()}`,
}));

export const TAX_KIND_LABEL = Object.fromEntries(
  taxKindOptions.map((option) => [option.value, option.label]),
);

export const taxComputationOptions = [
  { label: "Fixed", value: "FIXED" },
  { label: "Percentage", value: "PERCENTAGE" },
];

// A sales tax is usually a liability (PPN Keluaran is owed to the state), but a
// withholding tax on a sale is the opposite: the customer keeps the money and
// hands the business a receipt, so 110502 PPh 23 Dibayar Di Muka is an asset.
// Both directions therefore need both sides of the balance sheet.
export const salesAccountCategories = [
  "ACCOUNTS_PAYABLE",
  "OTHER_CURRENT_LIABILITIES",
  "OTHER_CURRENT_ASSETS",
  "INCOME",
  "EXPENSE",
  "OTHER_EXPENSE",
];

export const purchaseAccountCategories = [
  "OTHER_CURRENT_ASSETS",
  "OTHER_CURRENT_LIABILITIES",
  "ACCOUNTS_PAYABLE",
  "INCOME",
  "EXPENSE",
  "OTHER_EXPENSE",
];

// Whether a tax of this kind is normally CUT by the counterparty rather than
// added to the invoice. PPh 22 sits on the fence — a seller can collect it
// (dipungut) or a treasury buyer can withhold it — so it starts off and the
// user decides.
export const WITHHOLDING_BY_KIND = {
  PPN: false,
  PPNBM: false,
  PPH_23: true,
  PPH_4_2: true,
  PPH_15: true,
  PPH_22: false,
  OTHER: false,
};

// The account a tax of each kind posts to, by direction, most specific first.
// 220505/220506/220507 are only in the chart of accounts of businesses created
// after the withholding backend shipped, so an older chart falls through to
// 220599 Hutang Pajak Lainnya. An empty list means there is no sensible
// default: a final PPh 4(2) on a sale is a cost the user books to an account
// only they can choose.
export const DEFAULT_TAX_ACCOUNT_CODES = {
  PPN: { SALES: ["220500"], PURCHASES: ["110500"] },
  // PPnBM Masukan cannot be credited, so the backend folds a purchase one into
  // the cost of the goods; the account here only names where it lands.
  PPNBM: { SALES: ["220505", "220599"], PURCHASES: ["110500"] },
  PPH_23: { SALES: ["110502"], PURCHASES: ["220503"] },
  PPH_22: { SALES: ["110501"], PURCHASES: ["220502"] },
  PPH_4_2: { SALES: [], PURCHASES: ["220506", "220599"] },
  PPH_15: { SALES: [], PURCHASES: ["220507", "220599"] },
  OTHER: { SALES: [], PURCHASES: [] },
};

// A tax saved before `kind` existed is a PPN — that is all the form could make.
const DEFAULT_KIND_FOR_ACCOUNT = "PPN";

// The account to preselect for a tax of this kind and direction, or null when
// the chart has none of the candidates (or the kind has no sensible default).
// `accounts` are the option objects the account picker already built, so they
// carry `code`.
export const resolveDefaultTaxAccount = (accounts = [], kind, type) => {
  const codes =
    DEFAULT_TAX_ACCOUNT_CODES[kind || DEFAULT_KIND_FOR_ACCOUNT]?.[type] || [];
  for (const code of codes) {
    const match = accounts.find((account) => account.code === code);
    if (match) return match;
  }
  return null;
};

export const TAX_COMPUTATION = {
  FIXED: "Fixed",
  PERCENTAGE: "Percentage",
};

export const TAX_TYPE = {
  SALES: "Sales",
  PURCHASES: "Purchase",
};
